import { useEffect, useState } from "react";
import { api, formatApiError, formatCurrency, formatDate } from "@/lib/api";
import { Wallet, Landmark, CreditCard, CheckCircle2, Clock, XCircle, Receipt } from "lucide-react";
import { toast } from "sonner";

const METHOD_META = {
  cash: { label: "Contanti", Icon: Wallet, color: "amber" },
  bank: { label: "Bonifico", Icon: Landmark, color: "blue" },
  stripe: { label: "Carta", Icon: CreditCard, color: "indigo" },
  paypal: { label: "PayPal", Icon: CreditCard, color: "sky" },
};

const STATUS_META = {
  paid: { label: "Pagato", color: "emerald", Icon: CheckCircle2 },
  pending: { label: "In attesa", color: "amber", Icon: Clock },
  expired: { label: "Scaduto", color: "slate", Icon: XCircle },
  cancelled: { label: "Annullato", color: "red", Icon: XCircle },
};

export function PolicyPaymentsSection({ policyId, onOpenReceipt }) {
  const [txs, setTxs] = useState(null);

  useEffect(() => {
    if (!policyId) return;
    api.get(`/policies/${policyId}/payments`)
      .then((r) => setTxs(r.data))
      .catch((e) => toast.error(formatApiError(e)));
  }, [policyId]);

  if (txs === null) return null;
  const totalPaid = txs.filter((t) => t.payment_status === "paid").reduce((s, t) => s + (t.amount || 0), 0);

  return (
    <div className="col-span-2 border-t border-slate-200 pt-4 mt-2" data-testid="policy-payments-section">
      <div className="flex items-center justify-between mb-3">
        <p className="text-[11px] uppercase tracking-wide text-blue-700 font-semibold">Pagamenti ricevuti per questa polizza</p>
        <span className="text-xs text-slate-500">
          {txs.length === 0 ? "Nessuna transazione"
            : `${txs.length} transazion${txs.length === 1 ? "e" : "i"} · Totale incassato ${formatCurrency(totalPaid)}`}
        </span>
      </div>

      {txs.length === 0 ? (
        <div className="text-xs text-slate-400 bg-slate-50 border border-dashed border-slate-200 rounded p-4 text-center">
          Nessun pagamento ancora registrato per questa polizza.
        </div>
      ) : (
        <div className="space-y-1.5">
          {txs.map((tx) => {
            const m = METHOD_META[tx.method] || METHOD_META.cash;
            const s = STATUS_META[tx.payment_status] || STATUS_META.pending;
            const MIcon = m.Icon;
            const SIcon = s.Icon;
            return (
              <div
                key={tx.id}
                data-testid={`policy-tx-${tx.id}`}
                className="flex items-center gap-3 bg-white border border-slate-200 rounded-md px-3 py-2 text-sm hover:border-slate-300"
              >
                <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-${m.color}-50 text-${m.color}-700 border border-${m.color}-200`}>
                  <MIcon className="h-3 w-3" /> {m.label}
                </span>
                <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-${s.color}-50 text-${s.color}-700 border border-${s.color}-200`}>
                  <SIcon className="h-3 w-3" /> {s.label}
                </span>
                <div className="flex-1 min-w-0">
                  <span className="font-semibold tabular-nums">{formatCurrency(tx.amount)}</span>
                  <span className="text-slate-400 text-xs ml-2">
                    {tx.paid_at ? formatDate(tx.paid_at) : formatDate(tx.created_at)}
                  </span>
                  <span className="text-slate-400 text-[10px] ml-2 font-mono">{tx.reference}</span>
                </div>
                {onOpenReceipt && (
                  <button
                    data-testid={`policy-tx-receipt-${tx.id}`}
                    onClick={() => onOpenReceipt(tx)}
                    className="text-blue-600 hover:text-blue-800 inline-flex items-center gap-1 text-xs"
                  >
                    <Receipt className="h-3.5 w-3.5" /> Ricevuta
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
